/* The rig. A pose is a handful of numbers — head yaw / pitch / roll, a
   position, squash, how open the eyes are, where they look — and blend
   weights for the states. A Sim advances a pose through time: each state
   sets targets and wanders around them, runs its own events (a blink, a
   glance, a nod, a jump), and a state change eases from one set of targets
   to the next on a timed curve, so it starts and ends softly. */

import type { BotAvatarState } from './BotAvatar.types';

const STATES: BotAvatarState[] = ['default', 'sleeping'];

/* The jump: its height in body units (the body is 100 tall), seconds in
   the air, the stretch at the top and the squash on the ground, the crouch
   before take-off and the landing squash (one length), the hold at the
   deepest squash, the rise back to shape. */
const JUMP_HEIGHT = 16;
const JUMP_TIME = 0.68;
const JUMP_STRETCH = 1;
const JUMP_SQUASH = 1.15;
const JUMP_SQUASH_TIME = 0.24;
const JUMP_GROUND_TIME = 0.11;
const JUMP_RISE_TIME = 0.33;
const JUMP_LEAN = 6;
/* where in its time the landing squash reaches its deepest */
const SQUASH_PEAK = 2 / 7;
const jumpDuration = () =>
    JUMP_SQUASH_TIME + JUMP_TIME + SQUASH_PEAK * JUMP_SQUASH_TIME + JUMP_GROUND_TIME + JUMP_RISE_TIME + 0.05;

/* the rise from the deepest squash back to the body's own shape, 1 → 0:
   it leaves quickly with a long settle */
const risePulse = (v: number) => {
    if (v <= 0) return 1;
    if (v >= 1) return 0;
    const k = 4.2 * v;
    return (1 + k) * Math.exp(-k) - v * v * v * 0.078;
};

/* the landing squash over its time, 0–1 → its depth, 0–1 at the peak: a
   quick press that recovers without a wobble (a critically damped spring
   under a short push), faded out over the last part so it ends at nothing */
const squashPulse = (u: number) => {
    if (u <= 0 || u >= 1) return 0;
    const k = 7 * u;
    const x = (k * k * Math.exp(2 - k)) / 4;
    const tail = u > 0.85 ? 1 - (u - 0.85) / 0.15 : 1;
    return x * tail * tail * (3 - 2 * tail);
};
/* the weight settling through the hold, as a gain on the deepest squash:
   pressing up to a quarter deeper and back, so the hold's ends never step */
const groundShape = (v: number) => 1 + 0.25 * squashPulse(v);
/* the hop's shaping: squash on the ground, stretch at the top */
const hopSquash = (a: number) => Math.exp(-Math.pow(Math.min(Math.abs(a), Math.abs(a - 1)) / 0.11, 2));

export interface Pose {
    /** radians; yaw > 0 turns the face to the viewer's right, pitch > 0 looks up */
    yaw: number;
    pitch: number;
    roll: number;
    /** body-box units (the 100×100 design space) */
    y: number;
    sx: number;
    sy: number;
    /** how far the lids are down right now, 0 … 1: the sleeping state closes them through its own weight */
    blink: number;
    lookX: number;
    lookY: number;
    /** the breathing cycle, −1 … 1 */
    breath: number;
    /** blend weights: default, sleeping — they sum to 1 */
    w: [number, number];
}

const DEG = Math.PI / 180;
const TAU = Math.PI * 2;
/* How long a switch takes: dozing off is slow, waking a little quicker. */
const SWITCH_TO: Record<BotAvatarState, number> = { default: 1.2, sleeping: 1.4 };
const SWITCH_FROM_SLEEP = 1;

/* Deterministic per-instance randomness (mulberry32). */
function rng(seed: number): () => number {
    let a = (seed * 0x9e3779b1) >>> 0 || 1;
    return () => {
        a = (a + 0x6d2b79f5) >>> 0;
        let t = a;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

/** Exponential approach: `rate` per second, frame-rate independent. */
function approach(cur: number, target: number, rate: number, dt: number): number {
    return cur + (target - cur) * (1 - Math.exp(-rate * dt));
}

const easeInOut = (p: number) => (p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2);
/* gentler at both ends than the cubic: for drifting off and waking */
const easeSine = (p: number) => 0.5 - 0.5 * Math.cos(Math.PI * p);

/* A channel that picks a new target inside its range now and then and
   moves to it; the value never jumps. The head channels move as a lightly
   damped spring — the turn starts and ends softly, the way a head does —
   while the eyes dart with an exponential approach, the way eyes do. */
class Wander {
    value = 0;
    private vel = 0;
    private target = 0;
    private next = 0;
    private amp = 0;
    private holdMin = 0;
    private holdMax = 0;
    private rate = 0;
    constructor(
        private rand: () => number,
        private spring = false,
    ) {}
    update(t: number, dt: number) {
        if (t >= this.next) {
            this.target = (this.rand() * 2 - 1) * this.amp;
            this.next = t + this.holdMin + this.rand() * (this.holdMax - this.holdMin);
        }
        if (this.spring) {
            const w = this.rate * 1.6,
                z = 0.9;
            this.vel += (w * w * (this.target - this.value) - 2 * z * w * this.vel) * dt;
            this.value += this.vel * dt;
        } else this.value = approach(this.value, this.target, this.rate, dt);
    }
    /** aim at a value and stay there: the rig picks the next one */
    aim(v: number) {
        this.target = v;
        this.next = Infinity;
    }
    set(amp: number, holdMin: number, holdMax: number, rate: number) {
        this.amp = amp;
        this.holdMin = holdMin;
        this.holdMax = holdMax;
        this.rate = rate;
        this.next = 0;
    }
}

/* A one-shot event: progress 0 → 1 over its duration, then idle at -1. */
class OneShot {
    p = -1;
    constructor(public duration: number) {}
    fire() {
        this.p = 0;
    }
    get active() {
        return this.p >= 0;
    }
    update(dt: number) {
        if (this.p < 0) return;
        this.p += dt / this.duration;
        if (this.p >= 1) this.p = -1;
    }
}

/* The idle gaze: how far the head swings, and how long it stays. The
   sideways reach is the big one — a look to a corner reads as a turn of
   the head, with the rise and a slight tilt going along. */
const GAZE_YAW = 35 * DEG;
const GAZE_PITCH = 14 * DEG;
const GAZE_ROLL = 3.2 * DEG;
const GAZE_HOLD_MIN = 2.6;
const GAZE_HOLD_MAX = 4.4;

/* Resting targets per state. Everything the wanderers and events add sits
   on top of these. */
interface Rest {
    pitch: number;
    roll: number;
    y: number;
    lookX: number;
    lookY: number;
}
const REST: Record<BotAvatarState, Rest> = {
    default: { pitch: 0, roll: 0, y: 0, lookX: 0, lookY: 0 },
    sleeping: { pitch: -16 * DEG, roll: 6 * DEG, y: 3, lookX: 0, lookY: 1 },
};

export class Sim {
    readonly pose: Pose = {
        yaw: 0,
        pitch: 0,
        roll: 0,
        y: 0,
        sx: 1,
        sy: 1,
        blink: 0,
        lookX: 0,
        lookY: 0,
        breath: 0,
        w: [1, 0],
    };
    state: BotAvatarState = 'default';

    private rand: () => number;
    private t = 0;
    /* a state change: the weights it started from and its progress */
    private wFrom: [number, number] = [1, 0];
    private tr = 1;
    private trDuration = 1.2;
    private yawW: Wander;
    private pitchW: Wander;
    private rollW: Wander;
    private lookXW: Wander;
    private lookYW: Wander;
    private blink = new OneShot(0.17);
    private blinkAt: number;
    private blinkAgain = false;
    private dart = new OneShot(0.12);
    private dartAt: number;
    private dartX = 0;
    private dartY = 0;
    private jump = new OneShot(jumpDuration());
    private jumpSpin = 1;
    private jumpSide = 1;
    private nod = new OneShot(1.7);
    private nodAt: number;
    /* the jelly: a damped spring driven by how fast the head turns, so a
       sweep stretches the body and it wobbles back */
    private prevYaw = 0;
    private jelly = 0;
    private jellyV = 0;
    /** how far the eyes run ahead of a head turn */
    private gazeLead = 0;
    /** the idle gaze: where the head is looking, and when it moves on */
    private gazeDir: [number, number] = [0, 0];
    private gazeAt = 0;
    /** how far it turns to the side, 1 as the gaze has it */
    private turnK = 1;
    /** the breathing cycle's phase, in turns */
    private breathPhase = 0;
    /* the pointer, as an offset from the head in head-widths, and how much
       to follow it — both smoothed */
    private ptrX = 0;
    private ptrY = 0;
    private ptrS = 0;
    private ptrTargetX = 0;
    private ptrTargetY = 0;
    private ptrTargetS = 0;
    /* the smoothed yaw the head is turning to on its own */
    private baseYaw = 0;

    constructor(seed: number, state: BotAvatarState = 'default') {
        this.rand = rng(Math.floor(seed * 1e6) + 1);
        const r = this.rand;
        /* the state sets each channel's range and pace */
        this.yawW = new Wander(r, true);
        this.pitchW = new Wander(r, true);
        this.rollW = new Wander(r, true);
        this.lookXW = new Wander(r);
        this.lookYW = new Wander(r);
        /* every instance starts somewhere else in its loops */
        this.t = r() * 10;
        this.breathPhase = r();
        /* event timers count from that start, so nothing fires on the first tick */
        this.blinkAt = this.t + 1 + r() * 3;
        this.nodAt = this.t + 3 + r() * 4;
        this.dartAt = this.t + 1 + r() * 2;
        this.setState(state, true);
    }

    setState(next: BotAvatarState, immediate = false) {
        if (next === this.state && !immediate) return;
        const from = this.state;
        this.state = next;
        const w = this.pose.w;
        if (immediate) {
            for (let i = 0; i < STATES.length; i++) w[i] = STATES[i] === next ? 1 : 0;
            this.tr = 1;
        } else {
            this.wFrom = [w[0], w[1]];
            this.tr = 0;
            this.trDuration = from === 'sleeping' ? SWITCH_FROM_SLEEP : SWITCH_TO[next];
        }
        if (next === 'default') {
            /* the head takes its time: it holds a place, then swings to the
               next over about a second — no short quick jabs. The gaze picks
               where, so these carry the motion and their amplitude is unused. */
            this.yawW.set(GAZE_YAW, 2.6, 5.4, 2);
            this.pitchW.set(GAZE_PITCH, 2.8, 5.8, 1.8);
            this.rollW.set(GAZE_ROLL, 3.4, 6.6, 1.5);
            this.gazeAt = 0;
            this.gazeDir = [0, 0];
            /* the eyes keep their quickness: they dart, the head follows */
            this.lookXW.set(3.6, 0.6, 2.2, 13);
            this.lookYW.set(2.4, 0.6, 2.2, 13);
        } else {
            this.yawW.set(7 * DEG, 3, 6, 0.7);
            this.pitchW.set(3 * DEG, 3, 6, 0.7);
            this.rollW.set(2 * DEG, 3, 6, 0.6);
            this.lookXW.set(0, 2, 4, 2);
            this.lookYW.set(0, 2, 4, 2);
            this.nodAt = this.t + 2.5 + this.rand() * 4;
        }
    }

    /** Where the pointer is, relative to the head (−1 … 1 across a head
        width), and how strongly to follow it (0 lets go). */
    setPointer(x: number, y: number, strength: number) {
        this.ptrTargetX = Math.max(-1.2, Math.min(1.2, x));
        this.ptrTargetY = Math.max(-1.2, Math.min(1.2, y));
        this.ptrTargetS = Math.max(0, Math.min(1, strength));
    }

    /** A hop and a full turn, right now, whatever the state. */
    poke() {
        if (this.jump.active && this.jump.p < 0.6) return;
        this.jumpSide = this.rand() < 0.5 ? -1 : 1;
        this.jump.fire();
    }

    /** How far the head turns to the side while idle: 1 as the gaze has
        it, 0 keeps it facing forward. */
    setTurn(k: number) {
        const next = Math.max(0, k);
        if (next === this.turnK) return;
        this.turnK = next;
        /* take the new reach at once rather than at the next place */
        if (this.state === 'default') this.gazeAt = 0;
    }

    /** Whole turns made in the air. */
    setJumpSpin(spin: number) {
        this.jumpSpin = spin;
    }

    /* Where the head looks next. From a corner it mostly swings straight
       across to the opposite one — top right, stay, bottom left — now and
       then only sideways, or back to the middle for a beat. */
    private nextGaze(): [number, number] {
        const r = this.rand;
        const [px, py] = this.gazeDir;
        if (px !== 0 || py !== 0) {
            const p = r();
            if (p < 0.66) return [-px, -py];
            if (p < 0.85) return [-px, py];
            return [0, 0];
        }
        const corners: Array<[number, number]> = [
            [1, -1],
            [-1, 1],
            [-1, -1],
            [1, 1],
        ];
        return corners[Math.floor(r() * corners.length)];
    }

    /** Advance by `dt` seconds (already scaled by the speed). */
    update(dt: number) {
        dt = Math.min(dt, 0.05);
        this.t += dt;
        const t = this.t;
        const p = this.pose;
        const w = p.w;

        /* the state change eases from the weights it started with to the new
           state's on a sine curve: no kick at either end of a switch */
        if (this.tr < 1) {
            this.tr = Math.min(1, this.tr + dt / this.trDuration);
            const e = easeSine(this.tr);
            for (let i = 0; i < STATES.length; i++) {
                const target = STATES[i] === this.state ? 1 : 0;
                w[i] = this.wFrom[i] + (target - this.wFrom[i]) * e;
            }
        }
        const [wd, ws] = w;

        /* rest targets, blended */
        const rest = { pitch: 0, roll: 0, y: 0, lookX: 0, lookY: 0 };
        for (let i = 0; i < STATES.length; i++) {
            const r = REST[STATES[i]];
            rest.pitch += r.pitch * w[i];
            rest.roll += r.roll * w[i];
            rest.y += r.y * w[i];
            rest.lookX += r.lookX * w[i];
            rest.lookY += r.lookY * w[i];
        }

        /* the idle gaze: a place to look, a while to stay, then the swing to
           the next — the wanderers carry it on their own springs */
        if (this.state === 'default' && t >= this.gazeAt) {
            const [gx, gy] = this.nextGaze();
            this.gazeDir = [gx, gy];
            const reach = 0.84 + this.rand() * 0.16;
            this.yawW.aim(gx * GAZE_YAW * reach * this.turnK);
            this.pitchW.aim(gy * GAZE_PITCH * reach);
            this.rollW.aim(gx * GAZE_ROLL * reach * this.turnK);
            this.gazeAt = t + GAZE_HOLD_MIN + this.rand() * (GAZE_HOLD_MAX - GAZE_HOLD_MIN);
        }

        this.yawW.update(t, dt);
        this.pitchW.update(t, dt);
        this.rollW.update(t, dt);
        this.lookXW.update(t, dt);
        this.lookYW.update(t, dt);

        /* following the pointer: the eyes lead, the head turns after them,
           and the wander quietens while it lasts */
        this.ptrS = approach(this.ptrS, this.ptrTargetS, 8, dt);
        this.ptrX = approach(this.ptrX, this.ptrTargetX, 14, dt);
        this.ptrY = approach(this.ptrY, this.ptrTargetY, 14, dt);
        const ps = this.ptrS;
        const quiet = 1 - 0.75 * ps;

        this.baseYaw = approach(this.baseYaw, this.yawW.value * quiet + 22 * DEG * this.ptrX * ps, 5, dt);
        const basePitch = rest.pitch + this.pitchW.value * quiet - 12 * DEG * this.ptrY * ps;
        const baseRoll = rest.roll + this.rollW.value * quiet;
        const baseY = rest.y;
        const baseLookX = rest.lookX + this.lookXW.value * quiet + 4.5 * this.ptrX * ps;
        const baseLookY = rest.lookY + this.lookYW.value * quiet + 3 * this.ptrY * ps;

        let spin = 0,
            hopY = 0,
            sx = 1,
            sy = 1,
            pitchAdd = 0,
            rollAdd = 0,
            blinkClose = 0,
            lookXAdd = 0,
            lookYAdd = 0;

        /* blinks, awake only; a double blink now and then */
        if (t >= this.blinkAt && !this.blink.active && wd > 0.5) {
            this.blink.fire();
            this.blinkAgain = !this.blinkAgain && this.rand() < 0.22;
            this.blinkAt = t + (this.blinkAgain ? 0.28 : 2.2 + this.rand() * 2.6);
        }
        this.blink.update(dt);
        if (this.blink.active) blinkClose = Math.sin(Math.PI * this.blink.p);

        /* eye darts: a quick glance to the side and back, between the slower
           looks — the eyes have a life of their own */
        if (t >= this.dartAt && !this.dart.active && wd > 0.5) {
            this.dart.fire();
            this.dartX = (this.rand() * 2 - 1) * 4;
            this.dartY = (this.rand() * 2 - 1) * 2;
            this.dart.duration = 0.25 + this.rand() * 0.45;
            this.dartAt = t + 1.2 + this.rand() * 2.6;
        }
        this.dart.update(dt);
        if (this.dart.active) {
            const q = this.dart.p;
            /* snap out, hold, snap back */
            const hold = q < 0.15 ? q / 0.15 : q > 0.8 ? (1 - q) / 0.2 : 1;
            lookXAdd += this.dartX * hold * wd;
            lookYAdd += this.dartY * hold * wd;
        }

        this.jump.update(dt);
        if (this.jump.active) {
            /* the hop's own phase: below 0 the crouch, above 1 the recovery */
            const a = (this.jump.p * this.jump.duration - JUMP_SQUASH_TIME) / JUMP_TIME;
            const q = Math.min(1, Math.max(0, a));
            const arc = Math.sin(Math.PI * q);
            /* whole turns, eased in and out, no overshoot to snap back from */
            spin += TAU * this.jumpSpin * easeInOut(q);
            hopY -= JUMP_HEIGHT * arc;
            /* the landing in three parts: the press down to the deepest
               squash, the hold on the ground, then the rise back to the
               body's shape — all three meeting at the same depth, so
               nothing steps */
            const tl = (a - 1) * JUMP_TIME;
            const peakT = SQUASH_PEAK * JUMP_SQUASH_TIME;
            const rise = tl - peakT - JUMP_GROUND_TIME;
            const depth =
                tl <= peakT
                    ? squashPulse(tl / JUMP_SQUASH_TIME)
                    : rise <= 0
                      ? groundShape((tl - peakT) / JUMP_GROUND_TIME)
                      : risePulse(rise / JUMP_RISE_TIME);
            const crouch = (u: number) => u * u * (3 - 2 * u);
            const land =
                (a < 0
                    ? crouch(Math.max(0, 1 + (a * JUMP_TIME) / JUMP_SQUASH_TIME))
                    : tl > 0
                      ? depth
                      : a < 0.2
                        ? hopSquash(a)
                        : 0) * JUMP_SQUASH;
            sx += 0.16 * land - 0.06 * arc * JUMP_STRETCH;
            sy += -0.18 * land + 0.09 * arc * JUMP_STRETCH;
            rollAdd += this.jumpSide * JUMP_LEAN * DEG * arc;
        }

        /* sleeping: the head drops, then jerks back up */
        if (this.state === 'sleeping' && t >= this.nodAt && !this.nod.active) {
            this.nod.fire();
            this.nodAt = t + 4 + this.rand() * 4;
        }
        this.nod.update(dt);
        if (this.nod.active) {
            const q = this.nod.p;
            /* slow slide down, quick recovery */
            const dip = q < 0.72 ? easeInOut(q / 0.72) : 1 - easeInOut((q - 0.72) / 0.28);
            pitchAdd -= 13 * DEG * dip * ws;
        }

        /* breathing, always, deeper and slower asleep. The phase is integrated
           so the period can change with the state without the cycle jumping */
        this.breathPhase += dt / (3.6 + 1.2 * ws);
        const breath = Math.sin(this.breathPhase * TAU);
        p.breath = breath;
        sx += breath * (0.008 + 0.014 * ws);
        sy += breath * (0.012 + 0.02 * ws);
        const bob = Math.sin((t * TAU) / 3.4) * 2 * (1 - ws);

        p.yaw = this.baseYaw + spin;

        /* the jelly: the faster the head turns, the more the body stretches
           along the turn, on a spring that overshoots and settles. A jump's
           spin is left out: the landing spring handles it. */
        let dyaw = this.baseYaw - this.prevYaw;
        dyaw = ((((dyaw + Math.PI) % TAU) + TAU) % TAU) - Math.PI;
        this.prevYaw = this.baseYaw;
        const rate = dt > 0 ? Math.abs(dyaw) / dt : 0;
        /* the eyes lead a head turn, the way a gaze shift goes: they run
           ahead of it and come back as the head settles */
        const leadTarget = dt > 0 ? Math.max(-2.2, Math.min(2.2, (dyaw / dt) * 2.4)) : 0;
        this.gazeLead = approach(this.gazeLead, leadTarget, 9, dt);
        const jellyTarget = Math.min(0.22, 0.055 * rate);
        const omega = 16,
            zeta = 0.45;
        this.jellyV += (omega * omega * (jellyTarget - this.jelly) - 2 * zeta * omega * this.jellyV) * dt;
        this.jelly += this.jellyV * dt;
        const jelly = Math.max(-0.08, Math.min(0.28, this.jelly)) * 0.6;
        sx *= 1 + jelly;
        sy *= 1 - 0.55 * jelly;

        p.pitch = basePitch + pitchAdd;
        p.roll = baseRoll + rollAdd;
        p.y = baseY + hopY + bob;
        p.sx = sx;
        p.sy = sy;
        p.blink = blinkClose;
        p.lookX = baseLookX + lookXAdd + this.gazeLead;
        p.lookY = baseLookY + lookYAdd;
    }
}

/** The still pose of a state, for reduced motion. */
export function restPose(state: BotAvatarState): Pose {
    const r = REST[state];
    return {
        yaw: 0,
        pitch: r.pitch,
        roll: r.roll,
        y: r.y,
        sx: 1,
        sy: 1,
        blink: 0,
        lookX: r.lookX,
        lookY: r.lookY,
        breath: 0,
        w: STATES.map((s) => (s === state ? 1 : 0)) as [number, number],
    };
}
